import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spin, message, Descriptions, Tag, Space } from 'antd';
import { ArrowLeftOutlined, StarOutlined, DollarOutlined } from '@ant-design/icons';
import { keywordSearchAPI } from '../services/api';

interface SearchResult {
    id: number;
    asin: string;
    rank: number;
    brand: string;
    raw_data: Record<string, any>;
}

export const KeywordSearchDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [importData, setImportData] = useState<any>(null);
    const [results, setResults] = useState<SearchResult[]>([]);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            const [importRes, resultsRes] = await Promise.all([
                keywordSearchAPI.getImport(Number(id)),
                keywordSearchAPI.getResults(Number(id))
            ]);
            setImportData(importRes.data);
            setResults(resultsRes.data);
        } catch (error) {
            message.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) loadData();
    }, [id, loadData]);

    const columns = [
        { title: '#', dataIndex: 'rank', key: 'rank', width: 60 },
        {
            title: 'ASIN',
            dataIndex: 'asin',
            key: 'asin',
            width: 130,
            render: (text: string) => (
                <a href={`https://www.amazon.com/dp/${text}`} target="_blank" rel="noopener noreferrer">
                    {text}
                </a>
            )
        },
        {
            title: '品牌',
            key: 'brand',
            width: 120,
            render: (_: any, record: SearchResult) => record.raw_data?.['品牌'] || record.brand || '-'
        },
        {
            title: '价格',
            key: 'price',
            width: 80,
            align: 'right' as const,
            render: (_: any, record: SearchResult) => {
                const price = record.raw_data?.['价格($)'];
                return price ? <><DollarOutlined /> {price}</> : '-';
            }
        },
        {
            title: '评分',
            key: 'rating',
            width: 80,
            align: 'center' as const,
            render: (_: any, record: SearchResult) => {
                const rating = record.raw_data?.['评分'];
                if (!rating) return '-';
                const color = rating >= 4.5 ? '#52c41a' : rating >= 4 ? '#1890ff' : '#faad14';
                return <span style={{ color }}><StarOutlined /> {rating}</span>;
            }
        },
        {
            title: '评分数',
            key: 'reviews',
            width: 80,
            align: 'right' as const,
            render: (_: any, record: SearchResult) => record.raw_data?.['评分数'] || '-'
        },
        {
            title: '上架天数',
            key: 'days',
            width: 90,
            align: 'right' as const,
            render: (_: any, record: SearchResult) => {
                const days = record.raw_data?.['上架天数'];
                return days ? `${days}天` : '-';
            }
        },
        {
            title: '月销量',
            key: 'sales',
            width: 100,
            align: 'right' as const,
            render: (_: any, record: SearchResult) => record.raw_data?.['子体销量'] || record.raw_data?.['月销量'] || '-'
        },
        {
            title: '月销售额',
            key: 'revenue',
            width: 100,
            align: 'right' as const,
            render: (_: any, record: SearchResult) => {
                const rev = record.raw_data?.['子体销售额($)'] || record.raw_data?.['月销售额($)'];
                return rev ? `$${rev}` : '-';
            }
        },
        {
            title: 'FBA',
            key: 'fba',
            width: 70,
            align: 'right' as const,
            render: (_: any, record: SearchResult) => {
                const fba = record.raw_data?.['FBA($)'];
                return fba ? `$${fba}` : '-';
            }
        },
    ];

    if (loading) {
        return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>

            <Card title="导入信息" style={{ marginBottom: 16 }}>
                <Descriptions column={4}>
                    <Descriptions.Item label="关键词">
                        <strong>{importData?.keyword}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="站点">
                        <Tag color="blue">{importData?.site}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="搜索日期">
                        {importData?.search_date}
                    </Descriptions.Item>
                    <Descriptions.Item label="结果数">
                        {importData?.result_count}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title={`搜索结果 (${results.length}条)`}>
                <Table
                    columns={columns}
                    dataSource={results}
                    rowKey="id"
                    size="small"
                    scroll={{ x: 1000 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                />
            </Card>
        </div>
    );
};
